import { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info, Plus, TriangleAlert } from "lucide-react";
import { Controller, useForm, type Control } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Loading from "@/components/Loading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmailField } from "@/components/ui/email-field";
import { Field, FieldContent, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  createAggregatorSchema,
  EMPTY_AGGREGATOR_VALUES,
  type AdministratorRow,
  type AggregatorFormValues,
} from "../../schema";
import type { AggregatorCreationRequestFieldErrors, SelectOption } from "../../types";
import { AdministratorsTable } from "../AdministratorsTable";
import { FieldErrors } from "../FieldErrors";

export const AGGREGATOR_REQUEST_FORM_ID = "aggregator-creation-request-form";

type TextFieldName = Extract<
  keyof AggregatorFormValues,
  | "name"
  | "other_info"
  | "phone"
  | "wings_vendor_id"
  | "legal_address"
  | "physical_address"
  | "district"
  | "year_of_establishment"
  | "tier"
  | "membership_fee"
>;

interface AggregatorTextFieldProps {
  control: Control<AggregatorFormValues>;
  name: TextFieldName;
  label: string;
}

const AggregatorTextField = ({ control, name, label }: AggregatorTextFieldProps) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => (
      <Field>
        <FieldLabel htmlFor={name}>{label}</FieldLabel>
        <Input
          {...field}
          value={field.value ?? ""}
          id={name}
          aria-invalid={!!fieldState.error}
          className="border-border"
        />
        <FieldError>{fieldState.error?.message}</FieldError>
      </Field>
    )}
  />
);

interface AggregatorSelectFieldProps {
  control: Control<AggregatorFormValues>;
  name: Extract<keyof AggregatorFormValues, "legal_status" | "org_type" | "language">;
  label: string;
  options: SelectOption[];
}

const AggregatorSelectField = ({ control, name, label, options }: AggregatorSelectFieldProps) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => (
      <Field>
        <FieldLabel htmlFor={name}>{label}</FieldLabel>
        <Select value={field.value ?? ""} onValueChange={field.onChange}>
          <SelectTrigger
            id={name}
            className="w-full border-border"
            aria-invalid={!!fieldState.error}
          >
            <SelectValue placeholder={label} />
          </SelectTrigger>
          <SelectContent>
            {options.map(option => (
              <SelectItem key={option.value} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError>{fieldState.error?.message}</FieldError>
      </Field>
    )}
  />
);

interface FormProps {
  isFetching: boolean;
  errorMessage?: string;
  initialValues: AggregatorFormValues;
  administrators: AdministratorRow[];
  legalStatuses: SelectOption[];
  organizationTypes: SelectOption[];
  languages: SelectOption[];
  fieldErrors?: AggregatorCreationRequestFieldErrors;
  onValidate: (values: AggregatorFormValues) => void;
  onClickAddAdministrator: () => void;
  onClickEditAdministrator: (administrator: AdministratorRow) => void;
  onClickDeleteAdministrator: (administrator: AdministratorRow) => void;
}

export const Form = ({
  isFetching,
  errorMessage = "",
  initialValues,
  administrators,
  legalStatuses,
  organizationTypes,
  languages,
  fieldErrors,
  onValidate,
  onClickAddAdministrator,
  onClickEditAdministrator,
  onClickDeleteAdministrator,
}: FormProps) => {
  const { t } = useTranslation();

  const schema = useMemo(
    () => createAggregatorSchema(t("traderDetails.form.invalidEmailError")),
    [t]
  );

  const { control, handleSubmit, reset } = useForm<AggregatorFormValues>({
    defaultValues: EMPTY_AGGREGATOR_VALUES,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  if (isFetching) {
    return <Loading isVisible />;
  }

  if (errorMessage) {
    return (
      <Alert variant="destructive">
        <TriangleAlert />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <FieldErrors fieldErrors={fieldErrors} administrators={administrators} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>
                {t("representativeAggregatorCreationRequestDetails.form.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form
                id={AGGREGATOR_REQUEST_FORM_ID}
                noValidate
                className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
                onSubmit={handleSubmit(onValidate)}
              >
                <AggregatorTextField control={control} name="name" label={t("fieldName.name")} />
                <AggregatorTextField
                  control={control}
                  name="other_info"
                  label={t("fieldName.otherInformation")}
                />
                <EmailField control={control} name="contact_email" label={t("fieldName.email")} />
                <AggregatorTextField
                  control={control}
                  name="phone"
                  label={t("fieldName.phoneNumber")}
                />
                <AggregatorTextField
                  control={control}
                  name="wings_vendor_id"
                  label={t("fieldName.wingsId")}
                />
                <AggregatorTextField
                  control={control}
                  name="legal_address"
                  label={t("fieldName.legalAddress")}
                />
                <AggregatorTextField
                  control={control}
                  name="physical_address"
                  label={t("fieldName.physicalAddress")}
                />
                <AggregatorTextField
                  control={control}
                  name="district"
                  label={t("fieldName.district")}
                />
                <AggregatorSelectField
                  control={control}
                  name="legal_status"
                  label={t("fieldName.legalStatus")}
                  options={legalStatuses}
                />
                <AggregatorTextField
                  control={control}
                  name="year_of_establishment"
                  label={t("fieldName.yearOfEstablishment")}
                />
                <AggregatorTextField control={control} name="tier" label={t("fieldName.tier")} />
                <AggregatorSelectField
                  control={control}
                  name="org_type"
                  label={t("fieldName.organizationType")}
                  options={organizationTypes}
                />
                <AggregatorTextField
                  control={control}
                  name="membership_fee"
                  label={t("fieldName.membershipFee")}
                />
                <AggregatorSelectField
                  control={control}
                  name="language"
                  label={t("fieldName.language")}
                  options={languages}
                />
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("section.options")}</CardTitle>
            </CardHeader>
            <CardContent>
              <Controller
                control={control}
                name="membership_fee_needed"
                render={({ field }) => (
                  <Field orientation="horizontal">
                    <Switch
                      id="membership_fee_needed"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <FieldContent>
                      <FieldLabel htmlFor="membership_fee_needed" className="font-normal">
                        {t("fieldName.displayMembershipFee")}
                      </FieldLabel>
                    </FieldContent>
                  </Field>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("section.aggregators")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <WFPButton type="button" variant="secondary" onClick={onClickAddAdministrator}>
                <Plus className="size-4" aria-hidden="true" />
                {t("representativeAggregatorCreationRequestDetails.form.addAdministrator")}
              </WFPButton>
              <AdministratorsTable
                administrators={administrators}
                onClickEdit={onClickEditAdministrator}
                onClickDelete={onClickDeleteAdministrator}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <WFPButton type="submit" form={AGGREGATOR_REQUEST_FORM_ID}>
              {t("common.validate")}
            </WFPButton>
          </div>
        </div>

        <Card className="h-fit">
          <CardHeader>
            <CardTitle>{t("section.help")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Info />
              <AlertDescription>
                {t("representativeAggregatorCreationRequestDetails.form.info.one")}
              </AlertDescription>
            </Alert>
            <p>{t("representativeAggregatorCreationRequestDetails.form.info.two")}</p>
            <p>{t("representativeAggregatorCreationRequestDetails.form.info.three")}</p>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
